/**
 * Country vocabulary shared by both SPAs — types and pure presentation helpers.
 *
 * The country LIST is not here any more. It lives in ONE place for the whole
 * platform, `platform_settings.country_registry`, edited in Admin →
 * Configuración → Países, and every consumer reads it through
 * `hooks/useCountryRegistry` (the rules over it are in
 * `lib/countryRegistryRules`). There used to be four copies — this file's
 * `COUNTRY_CODES` / `PHONE_PREFIXES`, the admin's
 * signup list, the backend's region map and the signup allowlist — and they
 * disagreed.
 *
 * There is no per-country tax-id KIND any more (`20260978`): a workshop and a
 * customer have ONE «Nº de identificación fiscal», whatever the country.
 *
 * What stays here is what is NOT data:
 *   - the entry shape (`CountryEntry`), mirrored from
 *     `bolloprint_shared/country_registry.py`;
 *   - names and flags, which are derived from the ISO code at render time
 *     (`Intl.DisplayNames` ships every region name in every modern locale, so
 *     the registry does not store names).
 */

// ───────────── registry entry ─────────────

/** One tax a workshop in this country can pick. `rate` is a percentage. */
export interface CountryTax {
    name: string;
    rate: number;
}

/** The silo that serves a country. */
export type CountryRegion = 'eu' | 'us';

/**
 * Whether a workshop in the country may activate legal invoicing through the
 * platform's provider (`country_registry.INVOICING_MODES`):
 *  - `blocked`: not offered (the default — also what an entry without the key
 *    means);
 *  - `generic`: the provider's invoice is valid with no country module, so the
 *    activation wizard has no country-specific step;
 *  - `waiting`: a question is open with the provider; shown as pending, never
 *    activatable.
 */
export type CountryInvoicingMode = 'blocked' | 'generic' | 'waiting';

/** A dated change of the country's invoicing law and what it asks of us. */
export interface CountryInvoicingDeadline {
    /** ISO date, `YYYY-MM-DD`. */
    date: string;
    note: string;
}

/** The wording an invoice prints, in the country's language. */
export interface CountryInvoicingMentions {
    reverse_charge: string | null;
    export: string | null;
    small_business: string | null;
}

/** How strict a country is about the invoice's language: `any` language;
 *  `on_request` — another is valid, a translation may be asked for (the EU
 *  default); `official` — it MUST be one of `languages` (the PDF then goes out
 *  in the first one the provider prints). `null` = not verified. */
export type CountryLanguageRule = 'any' | 'on_request' | 'official';

/** Something else the law asks TODAY of an invoice or of its seller. */
export interface CountryInvoicingRequirement {
    /** A code of `REQUIREMENT_CODES` (Peppol, national platform, fiscalisation…). */
    code: string;
    /** Whom it touches: every sale, business buyers, consumers, public bodies. */
    scope: 'all' | 'b2b' | 'b2c' | 'b2g';
    /** Who answers it: we do (`covered`), the workshop outside the app
     *  (`workshop`), or nobody yet (`missing` — the country cannot open). */
    coverage: 'covered' | 'workshop' | 'missing';
    note: string;
}

/** The invoicing PROFILE of one country (mirrors `country_registry._inv`):
 *  everything its law asks of an invoice, as data (owner decision,
 *  2026-09-25). The code applies it without naming any country. */
/** `country_registry.E_INVOICE_FORMATS`. */
export type CountryEInvoiceFormat = 'zugferd';
/** `country_registry.TAX_ID_ROLES`. */
export type CountryTaxIdRole = 'vat' | 'national';
/** `country_registry.PAYMENT_QR_FORMATS`. */
export type CountryPaymentQr = 'swiss_qr_bill';
/** `country_registry.CLEARANCE_PLATFORMS`. */
export type CountryClearance = 'ksef';

export interface CountryInvoicing {
    mode: CountryInvoicingMode;
    /** Amount up to which a sale to a PRIVATE PERSON may be a simplified
     *  invoice without the buyer's details, in the country's currency; `null`
     *  = every invoice names its recipient. A company never gets one. */
    simplified_threshold: number | null;
    /** Whether that amount includes tax (gross) or not (net). */
    threshold_includes_tax: boolean;
    /** Customer fields a full invoice to a private person needs. */
    person_fields: string[];
    /** Customer fields an invoice to a company needs (its name always; its
     *  tax id everywhere by default, not in the UK). */
    company_fields: string[];
    mentions: CountryInvoicingMentions;
    /** Codes of the notices the activation wizard shows. */
    notices: string[];
    /** Dated changes of the law that oblige US to build something first
     *  (team information; an open country with one near opens an alarm). */
    deadlines: CountryInvoicingDeadline[];
    /** Languages an invoice of the country may be in (ISO 639-1). */
    languages: string[];
    language_rule: CountryLanguageRule | null;
    /** What else the law asks today (what comes later is a deadline). */
    requirements: CountryInvoicingRequirement[];
    /** The structured e-invoice generated and archived for the country's FULL
     *  invoices (`zugferd` in Germany); `null` = none. */
    e_invoice_format: CountryEInvoiceFormat | null;
    /** The workshop's own fields the country's full invoices need, beyond its
     *  fiscal block (`ISSUER_FIELDS`): asked on the wizard's country page. */
    issuer_fields: string[];
    /** A workshop under the small-business regime invoices WITHOUT a tax id
     *  (the UK: not registered for VAT, no number to print). */
    small_business_no_tax_id: boolean;
    /** Under the regime the workshop KEEPS its tax id but is not a VAT subject
     *  (Poland's art. 113): its invoices carry no VAT. */
    small_business_not_tax_subject: boolean;
    /** What the workshop's «identificación fiscal» IS there, which decides the
     *  provider field it prints in: `vat` (its VAT number), `national` (a
     *  national tax number, e.g. the Steuernummer) or `null` (no rule). */
    tax_id_role: CountryTaxIdRole | null;
    /** The local names of the tax id and of the company register number, for
     *  the workshop's form hints (national wording, never translated). */
    tax_id_name: string | null;
    company_number_name: string | null;
    /** The payment slip printed on every invoice (`swiss_qr_bill`), or none. */
    payment_qr: CountryPaymentQr | null;
    /** The platform every B2B invoice clears through, submitted by the provider (`ksef`), or none. */
    clearance: CountryClearance | null;
    /** How long the workshop must keep its invoices (informational). */
    retention_years: number | null;
    /** Free text for the admin panel. */
    notes: string | null;
}
// The mode list and the `blocked` default are values, so they live in
// `countryRegistryRules.ts` (`INVOICING_MODES`, `BLOCKED_INVOICING`).

/** One row of `platform_settings.country_registry` (see
 *  `bolloprint_shared/country_registry.py` for the rules each field obeys). */
export interface CountryEntry {
    /** ISO-3166 alpha-2, upper case. */
    code: string;
    /** A workshop may be created in this country (requires `region`). */
    signup: boolean;
    /** Offered in country pickers (customers, workshop, partners). */
    selectable: boolean;
    region: CountryRegion | null;
    /** Dial-in code (``+34``) or null. Several countries may share one. */
    phone_prefix: string | null;
    /** The national currency (ISO 4217): the one its VAT must be stated in. */
    currency: string | null;
    /** 0..6 taxes; the first one is the proposed default. */
    taxes: CountryTax[];
    /** Legal invoicing through the platform's provider; `blocked` by default. */
    invoicing: CountryInvoicing;
    /** How a workshop here writes dates and numbers unless it picks another
     *  (a `formatLocales` tag, `de-AT`); `null` = none, its language's. */
    format_locale?: string | null;
    /** In the EU VAT area, so a customer's VAT number can be checked in VIES.
     *  Added by the tenant API from a fixed list (not a registry setting),
     *  so the Admin's copy of the registry does not carry it. */
    vies?: boolean;
}

// ───────────── phone prefixes ─────────────

export interface PhonePrefix {
    /** Country ISO-2, used to pick the flag. */
    cc: string;
    /** Dial-in code as it must be stored (e.g. ``+34``). */
    code: string;
}

// ───────────── presentation helpers ─────────────

/**
 * Try to render a country code as a localized display name. Falls back to
 * the raw code if the runtime doesn't recognise it.
 */
export function countryDisplayName(code: string, locale: string): string {
    if (!code) return '';
    try {
        const dn = new Intl.DisplayNames([locale], { type: 'region' });
        return dn.of(code.toUpperCase()) ?? code;
    } catch {
        return code;
    }
}

/**
 * Build the Unicode "regional indicator" flag for an ISO-2 country code.
 * Works for every two-letter code and is rendered as the country flag in
 * modern browsers (Chrome/Edge/Safari/Firefox on macOS/Linux/Android).
 *
 * Note: Windows ships its system emoji font without flag glyphs. Chrome
 * on Windows falls back to a 2-letter rendering, which still reads fine
 * as a label.
 */
export function flagEmoji(code: string): string {
    if (!code || code.length !== 2) return '';
    const A = 0x1f1e6;
    const cc = code.toUpperCase();
    const c1 = cc.charCodeAt(0);
    const c2 = cc.charCodeAt(1);
    if (c1 < 65 || c1 > 90 || c2 < 65 || c2 > 90) return '';
    return String.fromCodePoint(A + (c1 - 65), A + (c2 - 65));
}
